import {
	ApartmentOutlined,
	CaretDownOutlined,
	CaretUpOutlined,
	DownOutlined,
	FilterFilled,
	LoadingOutlined,
	ProjectOutlined,
} from "@ant-design/icons";
import { Spin, Tooltip } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import {
	ControlCenterFilters,
	ControlCenterReport,
	FilterOptionsResponse,
} from "../../types/controlCenter.types";
import {
	formatDateDdMmmYyyy,
	formatDateTime,
} from "../../utils/date_formatter";
import CheckboxDropdown from "../CheckboxDropdown";
import SearchButton from "../SearchButton";
import CustomBadge from "../Badge/CustomBadge";

/* ─── Props ─── */
interface SectionData {
	items: ControlCenterReport[];
	total: number;
	offset: number;
	hasMore: boolean;
	isLoading: boolean;
}

const EMPTY_SECTION: SectionData = {
	items: [],
	total: 0,
	offset: 0,
	hasMore: false,
	isLoading: false,
};

type SectionKey = "open_reports" | "closed_reports" | "draft_reports";

interface ControlCenterReportTableProps {
	sectionData: Record<SectionKey, SectionData>;
	sectionCounts: Record<SectionKey, number>;
	loading?: boolean;
	isRefetching?: boolean;
	onRowClick?: (report: ControlCenterReport) => void;
	onLoadMore?: (section: SectionKey) => void;
	emailToNameMap?: Record<string, string>;
	onFiltersChange?: (filters: Partial<ControlCenterFilters>) => void;
	filters?: ControlCenterFilters;
	filterOptions?: FilterOptionsResponse;
}

/* ─── Constants ─── */
const statusTextMap: Record<string, string> = {
	draft: "Draft",
	in_review: "Initial review",
	in_progress: "In progress",
	final_review: "Final review",
	ready_to_close: "Approved - Open",
	changes_required: "Changes requested",
	revision_required: "Changes requested",
	closed_approved: "Approved and closed",
	closed_rejected: "Rejected and closed",
};

const ReportTypeTextMap: Record<string, string> = {
	audit: "Audit",
	incident_report: "Incident Report (IR)",
	moc: "Management of Change (MOC)",
	ofi: "Opportunity for Improvement (OFI)",
	non_conformance: "Non-Conformance Report (NCR)",
	lesson_learned: "Lesson Learned",
};

const SECTION_CONFIG: Record<
	SectionKey,
	{
		name: string;
		darkColor: string;
		lightColor: string;
		borderLeftColor: string;
	}
> = {
	draft_reports: {
		name: "Draft reports",
		darkColor: "bg-slate-800",
		lightColor: "bg-amber-50",
		borderLeftColor: "#F59E0B",
	},
	open_reports: {
		name: "Open reports",
		darkColor: "bg-slate-800",
		lightColor: "bg-cyan-50",
		borderLeftColor: "#3BDBD9",
	},
	closed_reports: {
		name: "Closed reports",
		darkColor: "bg-slate-800",
		lightColor: "bg-emerald-50",
		borderLeftColor: "#4ADE80",
	},
};

/* ─── Helpers ─── */
const resolveTaskAssignee = (
	report: ControlCenterReport,
	emailToNameMap: Record<string, string> = {}
): { name: string; email: string } | null => {
	if (!report.task_assigned_to) return null;
	const lowerEmail = report.task_assigned_to.toLowerCase();
	const nameFromMap = emailToNameMap[lowerEmail];
	return {
		name: nameFromMap || report.task_assigned_to,
		email: report.task_assigned_to,
	};
};

const AGING_THRESHOLD_DAYS = 6;

const isTaskAging = (report: ControlCenterReport): boolean => {
	if (report.task_deadline || !report.task_assigned_on) return false;
	const diffDays = Math.floor(
		(Date.now() - new Date(report.task_assigned_on).getTime()) /
			(1000 * 60 * 60 * 24)
	);
	return diffDays >= AGING_THRESHOLD_DAYS;
};

const getTaskStatusDotColor = (report: ControlCenterReport): string => {
	if (report.task_status === "completed") return "#4ADE80";
	if (!report.task_deadline) {
		return isTaskAging(report) ? "#FA3" : "#00BEBE";
	}
	const deadline = new Date(report.task_deadline);
	const now = new Date();
	const diffDays = Math.ceil(
		(deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
	);
	if (diffDays < 0) return "#FF4F4F";
	if (diffDays <= 3) return "#FA3";
	return "#00BEBE";
};

const getTaskStatusLabel = (report: ControlCenterReport): string => {
	if (report.task_status === "completed") return "Completed";
	if (!report.task_deadline) {
		return isTaskAging(report) ? "Require attention" : "On time";
	}
	const deadline = new Date(report.task_deadline);
	const now = new Date();
	const diffDays = Math.ceil(
		(deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
	);
	if (diffDays < 0) return "Overdue";
	if (diffDays <= 3) return "Require attention";
	return "On time";
};

const isTaskOverdue = (report: ControlCenterReport): boolean =>
	getTaskStatusDotColor(report) === "#FF4F4F";

const getDaysAgo = (dateStr: string): string => {
	const date = new Date(dateStr);
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
	if (diffDays <= 0) return "Today";
	if (diffDays === 1) return "1 day ago";
	return `${diffDays} days ago`;
};

/* Group items by report, preserving order of first appearance */
interface ReportGroup {
	reportId: string;
	report: ControlCenterReport;
	tasks: ControlCenterReport[];
}

function groupByReport(items: ControlCenterReport[]): ReportGroup[] {
	const map = new Map<string, ReportGroup>();
	items.forEach((item) => {
		const key = item._id;
		const existing = map.get(key);
		if (existing) {
			existing.tasks.push(item);
		} else {
			map.set(key, { reportId: key, report: item, tasks: [item] });
		}
	});
	return Array.from(map.values());
}

/* ─── Component ─── */
export default function ControlCenterReportTable({
	sectionData,
	sectionCounts,
	isRefetching = false,
	onRowClick,
	onLoadMore,
	emailToNameMap = {},
	onFiltersChange,
	filters,
	filterOptions,
}: ControlCenterReportTableProps): JSX.Element {
	const isDark = localStorage.getItem("theme") === "dark";

	const [sortConfig, setSortConfig] = useState<{
		key: "task_assigned_on" | "task_deadline" | null;
		direction: 1 | -1;
	}>({ key: null, direction: -1 });
	const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>(
		{
			open_reports: true,
			closed_reports: true,
		}
	);

	const handleSort = (key: "task_assigned_on" | "task_deadline") => {
		setSortConfig((prev) => {
			if (prev.key !== key) {
				return { key, direction: 1 };
			}
			if (prev.direction === 1) {
				return { key, direction: -1 };
			}
			return { key: null, direction: -1 };
		});
	};

	useEffect(() => {
		if (onFiltersChange) {
			onFiltersChange({
				sort_key: sortConfig.key || undefined,
				sort_direction: sortConfig.key ? sortConfig.direction : undefined,
			});
		}
	}, [sortConfig.key, sortConfig.direction, onFiltersChange]);

	/* ─── Filter options ─── */
	const {
		reportTypeFilterOptions,
		taskNameFilterOptions,
		statusFilterOptions,
		tenantFilterOptions,
	} = useMemo(() => {
		if (filterOptions) {
			const reportTypeFilterOptions = filterOptions.report_types.map(
				(value) => ({
					label: ReportTypeTextMap[value] || value,
					value,
				})
			);
			const taskNameFilterOptions = filterOptions.task_names.map((value) => ({
				label: value,
				value,
			}));
			const statusFilterOptions = filterOptions.combined_statuses.map(
				(value) => ({
					label: statusTextMap[value as keyof typeof statusTextMap] || value,
					value,
				})
			);
			const tenantFilterOptions =
				filterOptions.tenants?.map((value) => ({
					label: value,
					value,
				})) || [];
			return {
				reportTypeFilterOptions,
				taskNameFilterOptions,
				statusFilterOptions,
				tenantFilterOptions,
			};
		}

		const allItems = Object.values(sectionData).flatMap((s) => s.items);
		if (allItems.length === 0)
			return {
				reportTypeFilterOptions: [],
				taskNameFilterOptions: [],
				statusFilterOptions: [],
				tenantFilterOptions: [],
			};

		const reportTypeFilterOptions = Array.from(
			new Set(allItems.map((r) => r.report_type))
		)
			.filter(Boolean)
			.map((value) => ({
				label: ReportTypeTextMap[value] || value,
				value,
			}));

		const taskNameFilterOptions = Array.from(
			new Set(allItems.map((r) => r.task_name))
		)
			.filter(Boolean)
			.map((value) => ({
				label: value as string,
				value: value as string,
			}));

		const statusFilterOptions = Array.from(
			new Set(allItems.map((r) => r.report_combined_status))
		)
			.filter(Boolean)
			.map((value) => ({
				label: statusTextMap[value as keyof typeof statusTextMap] || value,
				value: value as string,
			}));

		const tenantFilterOptions = Array.from(
			new Set(allItems.map((r) => r.operator))
		)
			.filter(Boolean)
			.map((value) => ({
				label: value as string,
				value: value as string,
			}));

		return {
			reportTypeFilterOptions,
			taskNameFilterOptions,
			statusFilterOptions,
			tenantFilterOptions,
		};
	}, [filterOptions, sectionData]);

	return (
		<div
			className={`relative mx-auto rounded-s shadow-2xl w-full bg-transparent`}
		>
			<Spin spinning={isRefetching}>
				<table className="min-w-275 w-full">
					<thead className="bg-(--generic-table-header-bg-color)">
						<tr>
							<HeaderCell className="min-w-70 w-[30%]">
								<div className="flex items-center gap-2 justify-between">
									<SearchButton
										handleSearch={(value: { value: string }) => {
											if (onFiltersChange) {
												onFiltersChange({
													report_title: value.value || undefined,
												});
											}
										}}
										headerName="Report no and title"
										columnKey="report_title_and_tenant"
										value={filters?.report_title}
									/>
									<CheckboxDropdown
										options={tenantFilterOptions}
										onChange={(selectedValues) => {
											if (onFiltersChange) {
												onFiltersChange({
													tenants:
														selectedValues.length > 0
															? (selectedValues as string[])
															: undefined,
												});
											}
										}}
										defaultValue={filters?.tenants}
										Key="tenant"
										icon={<FilterFilled />}
										hideArrow
									/>
								</div>
							</HeaderCell>
							<HeaderCell className="min-w-50">
								<CheckboxDropdown
									options={reportTypeFilterOptions}
									onChange={(selectedValues) => {
										if (onFiltersChange) {
											onFiltersChange({
												report_types:
													selectedValues.length > 0
														? (selectedValues as string[])
														: undefined,
											});
										}
									}}
									defaultValue={filters?.report_types}
									Key="report_type"
									label="Type"
									icon={<FilterFilled />}
									hideArrow
								/>
							</HeaderCell>
							<HeaderCell className="min-w-50">
								<CheckboxDropdown
									options={statusFilterOptions}
									onChange={(selectedValues) => {
										if (onFiltersChange) {
											onFiltersChange({
												combined_statuses:
													selectedValues.length > 0
														? (selectedValues as string[])
														: undefined,
											});
										}
									}}
									defaultValue={filters?.combined_statuses}
									Key="combined_status"
									label="Report status"
									icon={<FilterFilled />}
									hideArrow
								/>
							</HeaderCell>
							<HeaderCell className="min-w-60 w-[30%]">
								<div className="flex items-center gap-2 justify-between">
									<span>Task</span>
									<CheckboxDropdown
										options={taskNameFilterOptions}
										onChange={(selectedValues) => {
											if (onFiltersChange) {
												onFiltersChange({
													task_names:
														selectedValues.length > 0
															? (selectedValues as string[])
															: undefined,
												});
											}
										}}
										defaultValue={filters?.task_names}
										Key="task_name"
										icon={<FilterFilled />}
										hideArrow
									/>
								</div>
							</HeaderCell>
							<HeaderCell className="min-w-60">
								<SearchButton
									handleSearch={(value: { value: string }) => {
										if (onFiltersChange) {
											onFiltersChange({
												assigned_to: value.value || undefined,
											});
										}
									}}
									headerName="Assigned to"
									columnKey="assigned_to"
									value={filters?.assigned_to}
								/>
							</HeaderCell>
							<SortableHeaderCell
								label="Assigned on"
								sortKey="task_assigned_on"
								sortConfig={sortConfig}
								onSort={handleSort}
								className="min-w-50"
							/>
							<SortableHeaderCell
								label="Due date"
								sortKey="task_deadline"
								sortConfig={sortConfig}
								onSort={handleSort}
								className="min-w-40"
							/>
						</tr>
					</thead>
					<tbody>
						{(Object.keys(SECTION_CONFIG) as SectionKey[]).map((sectionKey) => {
							const config = SECTION_CONFIG[sectionKey];
							const section = sectionData[sectionKey] ?? EMPTY_SECTION;
							const isOpen = openSections[sectionKey];
							const realCount = sectionCounts[sectionKey] ?? 0;
							const groups = groupByReport(section.items);

							return (
								<React.Fragment key={sectionKey}>
									<tr className={isDark ? config.darkColor : config.lightColor}>
										<td colSpan={7} className="px-0 py-0 text-sm font-semibold">
											<button
												onClick={() =>
													setOpenSections((prev) => ({
														...prev,
														[sectionKey]: !prev[sectionKey],
													}))
												}
												className="w-full flex items-center gap-2 px-4 py-3 text-left border-l-8 cursor-pointer"
												style={{
													borderLeftColor: config.borderLeftColor,
												}}
											>
												{config.name} ({realCount})
												<DownOutlined
													className={`text-xs ml-1 transition-transform ${
														isOpen ? "" : "-rotate-90"
													}`}
												/>
											</button>
										</td>
									</tr>
									{isOpen &&
										groups.map((group) =>
											group.tasks.map((task, taskIdx) => (
												<ReportRow
													key={`${sectionKey}-${task.task_id || task._id}-${taskIdx}`}
													group={group}
													task={task}
													showReportCell={taskIdx === 0}
													rowSpan={group.tasks.length}
													emailToNameMap={emailToNameMap}
													onClick={() => onRowClick?.(task)}
												/>
											))
										)}
									{isOpen && section.hasMore && (
										<tr className="bg-(--generic-table-header-bg-color)">
											<td
												colSpan={7}
												className="text-center py-3 text-(--default-btn-color) text-sm cursor-pointer hover:underline"
												onClick={() => {
													if (!section.isLoading) {
														onLoadMore?.(sectionKey);
													}
												}}
											>
												{section.isLoading ? (
													<span className="inline-flex items-center gap-2">
														<LoadingOutlined />
														Loading...
													</span>
												) : (
													"Load more..."
												)}
											</td>
										</tr>
									)}
								</React.Fragment>
							);
						})}
					</tbody>
				</table>
			</Spin>
		</div>
	);
}

/* ─── Sub-components ─── */
function HeaderCell({
	children,
	className = "",
}: {
	children: React.ReactNode;
	className?: string;
}) {
	return (
		<th
			className={`px-4 py-3 text-left font-medium text-(--generic-table-header-text-color) tracking-wide ${className}`}
		>
			{children}
		</th>
	);
}

function SortableHeaderCell({
	label,
	sortKey,
	sortConfig,
	onSort,
	className = "",
}: {
	label: string;
	sortKey: "task_assigned_on" | "task_deadline";
	sortConfig: { key: string | null; direction: 1 | -1 };
	onSort: (key: "task_assigned_on" | "task_deadline") => void;
	className?: string;
}) {
	const isActive = sortConfig.key === sortKey;
	return (
		<th
			className={`px-4 py-3 text-left font-medium text-(--generic-table-header-text-color) tracking-wide cursor-pointer select-none hover:opacity-80 transition-opacity ${className}`}
			onClick={() => onSort(sortKey)}
		>
			<div className="flex items-center gap-1 justify-between">
				{label}
				<span className="inline-flex flex-col leading-[0.5] ml-0.5">
					<CaretUpOutlined
						className={`text-[10px] ${
							isActive && sortConfig.direction === 1
								? "text-(--default-btn-color)"
								: "text-(--default-btn-color) opacity-40"
						}`}
					/>
					<CaretDownOutlined
						className={`text-[10px] ${
							isActive && sortConfig.direction === -1
								? "text-(--default-btn-color)"
								: "text-(--default-btn-color) opacity-40"
						}`}
					/>
				</span>
			</div>
		</th>
	);
}

function ReportRow({
	group,
	task,
	showReportCell,
	rowSpan,
	emailToNameMap,
	onClick,
}: {
	group: ReportGroup;
	task: ControlCenterReport;
	showReportCell: boolean;
	rowSpan: number;
	emailToNameMap: Record<string, string>;
	onClick?: () => void;
}) {
	const isDark = localStorage.getItem("theme") === "dark";

	const isOverdue = isTaskOverdue(task);

	const isGroupOverdue = group.tasks.some(isTaskOverdue);

	const firstColBg = isGroupOverdue
		? isDark
			? "bg-[#230000]"
			: "bg-[#ffc2c2]"
		: isDark
			? "bg-[#131344]"
			: "bg-(--ribbon-bg-color)";
	const otherColBg = isOverdue
		? isDark
			? "bg-[#230000]/40"
			: "bg-[#ffc2c2]/40"
		: isDark
			? "bg-[#131344]/40"
			: "bg-(--ribbon-bg-color)/40";
	const spannedStatusColBg = isGroupOverdue
		? isDark
			? "bg-[#230000]/40"
			: "bg-[#ffc2c2]/40"
		: isDark
			? "bg-[#131344]/40"
			: "bg-(--ribbon-bg-color)/40";
	const report = group.report;
	const assignee = resolveTaskAssignee(task, emailToNameMap);
	const assignedStr = assignee ? `${assignee.name} (${assignee.email})` : "—";
	const dotColor = getTaskStatusDotColor(task);

	return (
		<tr
			className={`border-b ${isDark ? "border-gray-500" : "border-gray-400"} hover:bg-(--table-hover-bg-color) cursor-pointer`}
			onClick={onClick}
		>
			{showReportCell && (
				<td
					rowSpan={rowSpan}
					className={`px-4 py-3 min-w-72 align-top ${firstColBg}`}
				>
					<div className="flex flex-col gap-2">
						<div className="text-(--default-btn-color) font-medium text-sm">
							{report.report_number} {report.report_title || report.title}
						</div>
						<Tooltip
							title="Tenant"
							placement="right"
							className={`flex items-center gap-1.5 text-xs ${isDark ? "text-white/60" : "text-black/60"} w-fit`}
						>
							<ApartmentOutlined />
							<span>{report.operator}</span>
						</Tooltip>
						{report.project_name && (
							<Tooltip
								title="Project"
								placement="right"
								className={`flex items-center gap-1.5 text-xs ${isDark ? "text-white/60" : "text-black/60"} w-fit`}
							>
								<ProjectOutlined />
								<span>{report.project_name}</span>
							</Tooltip>
						)}
					</div>
				</td>
			)}
			{showReportCell && (
				<td
					rowSpan={rowSpan}
					className={`px-4 py-3 text-sm align-top ${spannedStatusColBg}`}
				>
					{ReportTypeTextMap[report.report_type] || report.report_type || "—"}
				</td>
			)}
			{showReportCell && (
				<td
					rowSpan={rowSpan}
					className={`px-4 py-3 text-sm align-top ${spannedStatusColBg}`}
				>
					{statusTextMap[report.report_combined_status || ""] ||
						report.report_combined_status ||
						"—"}
				</td>
			)}
			<td className={`px-4 py-3 text-sm ${otherColBg}`}>
				<CustomBadge color={dotColor}>
					<Tooltip
						title={getTaskStatusLabel(task)}
						placement="right"
						className="w-fit"
					>
						<span
							className={task.task_status === "completed" ? "line-through" : ""}
						>
							{task.task_name || "—"}
						</span>
					</Tooltip>
				</CustomBadge>
			</td>
			<td className={`px-4 py-3 text-sm ${otherColBg}`}>{assignedStr}</td>
			<td className={`px-4 py-3 text-sm ${otherColBg}`}>
				{task.task_assigned_on ? formatDateTime(task.task_assigned_on) : "—"}
			</td>
			<td className={`px-4 py-3 text-sm ${otherColBg}`}>
				{task.task_deadline
					? formatDateDdMmmYyyy(task.task_deadline)
					: task.task_assigned_on
						? `Assigned ${getDaysAgo(task.task_assigned_on)}`
						: "—"}
			</td>
		</tr>
	);
}
